import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import {
  Search, ShieldAlert, Info, Loader2, Lock, TrendingUp,
  AlertTriangle, CheckCircle2, DollarSign, X,
} from 'lucide-react';

const money = (n) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(Number(n) || 0);
const pct = (n) => `${(Number(n) || 0).toFixed(2)}%`;

/**
 * Mutual Fund Window Panel.
 *
 * Eligibility: $10,000 minimum retirement balance.
 * Allocation rule: max 25% in window, min 75% in core UniFed Core funds.
 * Fees: $55/yr maintenance + $28.75/trade.
 * Universe: ~5,000 mutual funds.
 *
 * Citations: 5 U.S.C. § 8438; 5 C.F.R. Part 1601; Recordkeeping Bulletin 22-1
 */
export const MutualFundWindowPanel = () => {
  const [elig, setElig] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [totalWindow, setTotalWindow] = useState(0);
  const [pctOfTotal, setPctOfTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [feeAcked, setFeeAcked] = useState(false);

  // Search state
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Order modal state
  const [orderFund, setOrderFund] = useState(null);
  const [orderSide, setOrderSide] = useState('buy');
  const [orderAmount, setOrderAmount] = useState('');
  const [orderErr, setOrderErr] = useState('');
  const [orderSubmitting, setOrderSubmitting] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState('');

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [e, h] = await Promise.allSettled([
        api.get('/participant/mutual-fund-window/eligibility'),
        api.get('/participant/mutual-fund-window/holdings'),
      ]);
      if (e.status === 'fulfilled') setElig(e.value.data);
      if (h.status === 'fulfilled') {
        setHoldings(h.value.data?.holdings || []);
        setTotalWindow(h.value.data?.totalWindowValue || 0);
        setPctOfTotal(h.value.data?.pctOfTotal || 0);
      }
    } finally {
      setLoading(false);
    }
  };

  const runSearch = useCallback(async (q) => {
    setSearching(true);
    try {
      const res = await api.get(`/market/mutual-fund-window/search`, { params: { query: q } });
      setSearchResults(res.data?.results || []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (!feeAcked || !elig?.eligible) return;
    const t = setTimeout(() => { runSearch(query); }, 250);
    return () => clearTimeout(t);
  }, [query, feeAcked, elig, runSearch]);

  const openOrder = (fund, side = 'buy') => {
    setOrderFund(fund);
    setOrderSide(side);
    setOrderAmount('');
    setOrderErr('');
    setOrderSuccess('');
  };

  const submitOrder = async () => {
    setOrderErr('');
    const amt = parseFloat(orderAmount);
    if (!amt || amt <= 0) { setOrderErr('Enter a valid dollar amount.'); return; }

    // Client-side 25% check for buys
    if (orderSide === 'buy' && elig?.split) {
      if ((elig.split.windowValue + amt) > elig.split.maxWindowAllowed + 0.01) {
        setOrderErr(
          `75/25 rule violation — window cap ${money(elig.split.maxWindowAllowed)}. Available headroom: ${money(elig.split.headroom)}.`,
        );
        return;
      }
    }

    setOrderSubmitting(true);
    try {
      const res = await api.post('/participant/mutual-fund-window/orders', {
        side: orderSide,
        ticker: orderFund.ticker,
        amount: amt,
      });
      setOrderSuccess(res.data?.message || 'Order filled.');
      await loadAll();
    } catch (e) {
      setOrderErr(e.response?.data?.detail || 'Order failed.');
    } finally {
      setOrderSubmitting(false);
    }
  };

  const inputCls = "w-full h-10 px-3 bg-white border border-[#D1D5DB] rounded text-sm text-[#1B2A4A] focus:outline-none focus:border-[#1B2A4A] focus:ring-1 focus:ring-[#1B2A4A]/20";

  if (loading) {
    return (
      <div className="panel p-6" data-testid="mfw-panel-loading">
        <div className="flex items-center gap-2 text-[#6B7280] text-sm">
          <Loader2 size={14} className="animate-spin" /> Loading Mutual Fund Window…
        </div>
      </div>
    );
  }

  // ─── INELIGIBLE ──────────────────────────────────────────────
  if (elig && !elig.eligible) {
    return (
      <div className="panel p-5" data-testid="mfw-panel-ineligible">
        <div className="flex items-center gap-2 mb-3">
          <Lock size={16} className="text-[#6B7280]" />
          <h3 className="text-sm font-semibold text-[#1B2A4A]">Mutual Fund Window</h3>
          <span className="ml-auto text-[10px] text-[#9CA3AF] font-mono">Recordkeeping Bulletin 22-1</span>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded px-4 py-3 flex items-start gap-2">
          <AlertTriangle size={14} className="text-amber-600 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-800 leading-relaxed">
            <strong>Not yet eligible.</strong> {elig.reason}
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div className="rounded border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280]">Current Balance</div>
            <div className="font-mono text-sm text-[#1B2A4A] mt-0.5" data-testid="mfw-current-balance">{money(elig.currentBalance)}</div>
          </div>
          <div className="rounded border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280]">Shortfall to ${elig.minimumRequired.toLocaleString()}</div>
            <div className="font-mono text-sm text-red-700 mt-0.5" data-testid="mfw-shortfall">{money(elig.shortfall)}</div>
          </div>
        </div>
      </div>
    );
  }

  // ─── FEE GATE ────────────────────────────────────────────────
  if (!feeAcked) {
    return (
      <div className="panel p-5" data-testid="mfw-fee-gate">
        <div className="flex items-center gap-2 mb-3">
          <DollarSign size={16} className="text-[#1B2A4A]" />
          <h3 className="text-sm font-semibold text-[#1B2A4A]">Mutual Fund Window — Fee Disclosure</h3>
          <span className="ml-auto text-[10px] text-[#9CA3AF] font-mono">Recordkeeping Bulletin 22-1</span>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded px-4 py-3 text-xs text-blue-900 leading-relaxed" data-testid="mfw-fee-disclosure">
          <p className="font-semibold mb-2">Before accessing the Mutual Fund Window, review the fee schedule:</p>
          <ul className="space-y-1 ml-4 list-disc">
            <li>Annual maintenance fee: <span className="font-mono font-semibold">{money(elig.annualFee)}</span></li>
            <li>Per-transaction fee: <span className="font-mono font-semibold">{money(elig.perTransactionFee)}</span></li>
            <li>Approximately <span className="font-mono font-semibold">{elig.fundUniverseSize.toLocaleString()}</span> mutual funds are available.</li>
            <li>Fees are deducted directly from your account.</li>
          </ul>
          <p className="mt-2 text-[10px] text-blue-700">
            Allocation cap: 25% of your retirement balance may be in the window. 75% must remain in core UniFed Core funds at all times.
          </p>
        </div>
        <button
          data-testid="mfw-ack-fees-btn"
          onClick={() => setFeeAcked(true)}
          className="mt-3 px-4 py-2 bg-[#1B2A4A] text-white text-sm rounded hover:bg-[#2D3F5F]"
        >
          I Acknowledge — Continue
        </button>
      </div>
    );
  }

  // ─── MAIN ────────────────────────────────────────────────────
  const split = elig.split || { corePct: 100, windowPct: 0, windowValue: 0, coreBalance: elig.currentBalance, maxWindowAllowed: elig.currentBalance * 0.25, headroom: elig.currentBalance * 0.25 };

  return (
    <div className="space-y-4" data-testid="mfw-panel">
      <div className="panel p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <TrendingUp size={16} className="text-[#1B2A4A]" />
            <h3 className="text-sm font-semibold text-[#1B2A4A]">Mutual Fund Window</h3>
          </div>
          <span className="text-[10px] text-[#9CA3AF] font-mono">5 U.S.C. § 8438 · Recordkeeping Bulletin 22-1</span>
        </div>

        {/* Allocation split bar */}
        <div className="mb-4" data-testid="mfw-split-bar">
          <div className="flex items-center justify-between text-[11px] mb-1">
            <span className="text-[#6B7280] font-medium">Allocation Split (max 25% window / min 75% core)</span>
            <span className="font-mono text-[#1B2A4A]" data-testid="mfw-split-text">
              {pct(split.corePct)} core / {pct(split.windowPct)} window
            </span>
          </div>
          <div className="h-2.5 bg-[#F0F1F3] rounded-full overflow-hidden flex">
            <div
              className="bg-[#1B2A4A]"
              style={{ width: `${Math.min(split.corePct, 100)}%` }}
              title={`Core: ${money(split.coreBalance)}`}
            />
            <div
              className={`${split.windowPct > 25 ? 'bg-red-600' : 'bg-emerald-600'}`}
              style={{ width: `${Math.min(split.windowPct, 100)}%` }}
              title={`Window: ${money(split.windowValue)}`}
            />
          </div>
          <div className="flex items-center gap-3 mt-1 text-[10px] text-[#6B7280] font-mono">
            <span>Window cap: {money(split.maxWindowAllowed)}</span>
            <span>•</span>
            <span>Headroom: <span className="text-emerald-700 font-semibold">{money(split.headroom)}</span></span>
          </div>
        </div>

        {/* Summary cells */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Cell label="Total Retirement Balance" value={money(elig.currentBalance)} />
          <Cell label="Window Value" value={money(split.windowValue)} />
          <Cell label="% of Total" value={pct(split.windowPct)} />
          <Cell label="Annual Fee" value={money(elig.annualFee)} tone="amber" />
        </div>

        {/* Holdings */}
        <div className="mt-5 border-t border-[#E5E7EB] pt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280]">Window Holdings</span>
            <span className="text-[10px] text-[#9CA3AF] font-mono" data-testid="mfw-holdings-count">{holdings.length} position{holdings.length === 1 ? '' : 's'}</span>
          </div>
          {holdings.length === 0 ? (
            <div className="bg-[#F9FAFB] border border-dashed border-[#D1D5DB] rounded px-4 py-6 text-center text-xs text-[#6B7280]" data-testid="mfw-holdings-empty">
              No positions yet. Search for a mutual fund below to place your first order.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]" data-testid="mfw-holdings-table">
                <thead>
                  <tr className="text-left text-[#6B7280] border-b border-[#E5E7EB]">
                    <th className="py-1.5 pr-3 font-medium">Fund</th>
                    <th className="py-1.5 pr-3 font-medium">Ticker</th>
                    <th className="py-1.5 pr-3 font-medium text-right">Value</th>
                    <th className="py-1.5 pr-3 font-medium text-right">Day Δ</th>
                    <th className="py-1.5 pr-3 font-medium text-right">YTD</th>
                    <th className="py-1.5 pr-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {holdings.map(h => (
                    <tr key={h.id} className="border-b border-[#F3F4F6] last:border-0">
                      <td className="py-2 pr-3 text-[#1B2A4A]">{h.fundName}<div className="text-[10px] text-[#9CA3AF]">{h.category}</div></td>
                      <td className="py-2 pr-3 font-mono text-[#1B2A4A]">{h.ticker}</td>
                      <td className="py-2 pr-3 font-mono text-right text-[#1B2A4A]">{money(h.value)}</td>
                      <td className={`py-2 pr-3 font-mono text-right ${h.dayChange >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                        {h.dayChange >= 0 ? '+' : ''}{money(h.dayChange)} ({pct(h.dayChangePct)})
                      </td>
                      <td className={`py-2 pr-3 font-mono text-right ${h.ytd >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{pct(h.ytd)}</td>
                      <td className="py-2 pr-3 text-right">
                        <button onClick={() => openOrder({ ticker: h.ticker, fundName: h.fundName }, 'sell')} className="px-2 py-1 text-[10px] font-medium text-red-700 border border-red-200 rounded hover:bg-red-50">Sell</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Fund Search */}
        <div className="mt-5 border-t border-[#E5E7EB] pt-4">
          <label className="block text-[10px] uppercase tracking-wider font-semibold text-[#6B7280] mb-1.5">
            Search {elig.fundUniverseSize.toLocaleString()}+ Funds
          </label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none" />
            <input
              data-testid="mfw-search-input"
              type="text"
              placeholder="Search by ticker or fund name (e.g., VTSAX, bond, growth)"
              value={query}
              onChange={e => setQuery(e.target.value)}
              className={`${inputCls} pl-9`}
            />
          </div>
          {searching && (
            <div className="flex items-center gap-2 mt-2 text-[10px] text-[#6B7280]"><Loader2 size={11} className="animate-spin" /> Searching…</div>
          )}
          {searchResults.length > 0 && (
            <div className="mt-2 border border-[#E5E7EB] rounded overflow-hidden" data-testid="mfw-search-results">
              <table className="w-full text-[11px]">
                <thead className="bg-[#F9FAFB]">
                  <tr className="text-left text-[#6B7280]">
                    <th className="py-1.5 px-3 font-medium">Ticker</th>
                    <th className="py-1.5 px-3 font-medium">Fund</th>
                    <th className="py-1.5 px-3 font-medium text-right">Expense</th>
                    <th className="py-1.5 px-3 font-medium">Category</th>
                    <th className="py-1.5 px-3 font-medium text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults.map(f => (
                    <tr key={f.ticker} className="border-t border-[#F3F4F6]" data-testid={`mfw-search-row-${f.ticker}`}>
                      <td className="py-1.5 px-3 font-mono text-[#1B2A4A]">{f.ticker}</td>
                      <td className="py-1.5 px-3 text-[#1B2A4A]">{f.fundName}</td>
                      <td className="py-1.5 px-3 font-mono text-right text-[#6B7280]">{(f.expenseRatio * 100).toFixed(2)}%</td>
                      <td className="py-1.5 px-3 text-[#6B7280]">{f.category}</td>
                      <td className="py-1.5 px-3 text-right">
                        <button
                          data-testid={`mfw-buy-${f.ticker}`}
                          onClick={() => openOrder(f, 'buy')}
                          className="px-2 py-1 text-[10px] font-medium text-emerald-700 border border-emerald-200 rounded hover:bg-emerald-50"
                        >
                          Buy
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Fee reminder */}
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded px-3 py-2 flex items-start gap-2">
          <ShieldAlert size={12} className="text-amber-600 mt-0.5 shrink-0" />
          <p className="text-[10px] text-amber-800 leading-relaxed">
            Every buy or sell incurs a <span className="font-mono font-semibold">{money(elig.perTransactionFee)}</span> transaction fee plus an
            annual maintenance fee of <span className="font-mono font-semibold">{money(elig.annualFee)}</span>. Fees are deducted from your retirement account.
          </p>
        </div>

        {/* Citations */}
        <div className="mt-2 text-[9px] text-[#9CA3AF] font-mono">{elig.citations}</div>
      </div>

      {/* Order Modal */}
      {orderFund && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="mfw-order-modal">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-[#1B2A4A]">
                {orderSide === 'buy' ? 'Buy' : 'Sell'} — {orderFund.ticker}
              </h4>
              <button onClick={() => setOrderFund(null)} className="text-[#9CA3AF] hover:text-[#1B2A4A]" data-testid="mfw-order-close"><X size={16} /></button>
            </div>
            <p className="text-xs text-[#6B7280] mb-3">{orderFund.fundName}</p>

            <label className="block text-xs font-medium text-[#1B2A4A] mb-1">Dollar Amount</label>
            <input
              data-testid="mfw-order-amount"
              type="number"
              min={1}
              step={100}
              value={orderAmount}
              onChange={e => setOrderAmount(e.target.value)}
              placeholder={orderSide === 'buy' ? `Max ${money(split.headroom)}` : '0.00'}
              className={inputCls}
            />
            {orderSide === 'buy' && (
              <p className="text-[10px] text-[#9CA3AF] mt-1 font-mono">Available headroom: {money(split.headroom)}</p>
            )}

            <div className="mt-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded px-3 py-2 text-[11px] text-[#6B7280]">
              <div className="flex items-center justify-between"><span>Transaction fee</span><span className="font-mono">{money(elig.perTransactionFee)}</span></div>
            </div>

            {orderErr && (
              <div className="mt-3 flex items-start gap-2 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="mfw-order-error">
                <AlertTriangle size={12} className="text-red-600 mt-0.5 shrink-0" />
                <span className="text-[11px] text-red-800">{orderErr}</span>
              </div>
            )}

            {orderSuccess && (
              <div className="mt-3 flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded px-3 py-2" data-testid="mfw-order-success">
                <CheckCircle2 size={12} className="text-emerald-600 mt-0.5 shrink-0" />
                <span className="text-[11px] text-emerald-800">{orderSuccess}</span>
              </div>
            )}

            <div className="mt-4 flex gap-2">
              <button
                data-testid="mfw-order-submit"
                onClick={submitOrder}
                disabled={orderSubmitting || !orderAmount}
                className={`flex-1 px-4 py-2 text-sm rounded text-white ${orderSide === 'buy' ? 'bg-emerald-700 hover:bg-emerald-800' : 'bg-red-700 hover:bg-red-800'} disabled:opacity-50 flex items-center justify-center gap-2`}
              >
                {orderSubmitting ? <Loader2 size={14} className="animate-spin" /> : null}
                Confirm {orderSide === 'buy' ? 'Buy' : 'Sell'}
              </button>
              <button onClick={() => setOrderFund(null)} className="px-4 py-2 border border-[#D1D5DB] text-sm rounded text-[#6B7280] hover:bg-gray-50">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Cell = ({ label, value, tone }) => {
  const toneCls =
    tone === 'amber' ? 'text-amber-700' :
    tone === 'red' ? 'text-red-700' :
    tone === 'emerald' ? 'text-emerald-700' :
    'text-[#1B2A4A]';
  return (
    <div className="rounded border border-[#E5E7EB] bg-white px-3 py-2">
      <div className="text-[9px] uppercase tracking-wider font-semibold text-[#6B7280]">{label}</div>
      <div className={`font-mono text-sm mt-0.5 ${toneCls}`}>{value}</div>
    </div>
  );
};
